import {
  GetIngestionDocumentFragment,
  RuleType,
} from '@client/graphql/__generated__/types';
import { groupBy } from 'lodash';
import type { ComplianceIngestionDocumentValidation } from '../Compliance.types';
import { createFieldKeyToLabelMapping } from './fieldMapping.utils';
import { ValidationResult } from './validation.utils';

export interface FieldComparisonData {
  fieldKey: string;
  fieldLabel?: string;
  aiResults: ValidationResult[];
  regularResults: ValidationResult[];
  changeType: 'added' | 'removed' | 'modified' | 'unchanged';
  improvementType?:
    | 'false_positive_removed'
    | 'missing_error_added'
    | 'severity_changed';
  aiErrorCount: number;
  regularErrorCount: number;
  aiWarningCount: number;
  regularWarningCount: number;
  reductionCount: number;
  documentVersionFieldUuid?: string;
}

export interface FieldComparisonSummary {
  fieldComparisons: FieldComparisonData[];
  totalFields: number;
  improvedFields: number;
  unchangedFields: number;
  degradedFields: number;
  hasAnyChanges: boolean;
}

/**
 * Groups validation results by field key
 */
function groupValidationResultsByField(
  validationResults: ValidationResult[]
): Record<string, ValidationResult[]> {
  return groupBy(validationResults, (result) => {
    return (
      result.fieldMappingKey || result.documentVersionFieldUuid || 'unknown'
    );
  });
}

/**
 * Filters validation results into errors and warnings
 */
function filterResultsByType(results: ValidationResult[]): {
  errors: ValidationResult[];
  warnings: ValidationResult[];
} {
  const errors = results.filter(
    (r) => r.output && r.ruleType !== RuleType.WARNING
  );
  const warnings = results.filter(
    (r) => r.output && r.ruleType === RuleType.WARNING
  );

  return { errors, warnings };
}

/**
 * Determines the change type between AI and regular validation results
 */
function categorizeFieldChanges(
  aiResults: ValidationResult[],
  regularResults: ValidationResult[]
): {
  changeType: FieldComparisonData['changeType'];
  improvementType?: FieldComparisonData['improvementType'];
} {
  const { errors: aiErrors, warnings: aiWarnings } =
    filterResultsByType(aiResults);
  const { errors: regularErrors, warnings: regularWarnings } =
    filterResultsByType(regularResults);

  const aiCount = aiErrors.length + aiWarnings.length;
  const regularCount = regularErrors.length + regularWarnings.length;

  if (aiCount === 0 && regularCount === 0) {
    return { changeType: 'unchanged' };
  }

  if (aiCount === 0 && regularCount > 0) {
    return { changeType: 'removed', improvementType: 'false_positive_removed' };
  }

  if (aiCount > 0 && regularCount === 0) {
    return { changeType: 'added', improvementType: 'missing_error_added' };
  }

  if (aiCount !== regularCount) {
    return { changeType: 'modified', improvementType: 'severity_changed' };
  }

  // Check if messages or severity changed
  const aiMessages = new Set(aiResults.map((r) => r.message));
  const regularMessages = new Set(regularResults.map((r) => r.message));
  const messagesChanged =
    aiMessages.size !== regularMessages.size ||
    [...aiMessages].some((msg) => !regularMessages.has(msg));

  if (messagesChanged) {
    return { changeType: 'modified', improvementType: 'severity_changed' };
  }

  return { changeType: 'unchanged' };
}

/**
 * Compares AI vs regular validation results at the field level
 */
export function getFieldLevelComparisonData(
  document: GetIngestionDocumentFragment,
  validationForDocument: ComplianceIngestionDocumentValidation
): FieldComparisonSummary {
  const contractDocumentUuids = new Set(
    document.contractDocuments?.map((cd) => cd.id) || []
  );

  const fieldKeyToLabel = createFieldKeyToLabelMapping(document);
  const allAiResults: ValidationResult[] = [];
  const allRegularResults: ValidationResult[] = [];

  // Collect all validation results for this document
  const aiValidationResults =
    validationForDocument.aiValidationResults as unknown as
      | ValidationResult[]
      | undefined;
  const validationResults =
    validationForDocument.validationResults as unknown as
      | ValidationResult[]
      | undefined;

  if (aiValidationResults) {
    allAiResults.push(
      ...aiValidationResults.filter(
        (result) =>
          result.contractDocumentUuid &&
          contractDocumentUuids.has(result.contractDocumentUuid)
      )
    );
  }

  if (validationResults) {
    allRegularResults.push(
      ...validationResults.filter(
        (result) =>
          result.contractDocumentUuid &&
          contractDocumentUuids.has(result.contractDocumentUuid)
      )
    );
  }

  // Group results by field
  const aiResultsByField = groupValidationResultsByField(allAiResults);
  const regularResultsByField =
    groupValidationResultsByField(allRegularResults);

  // Get all unique field keys
  const allFieldKeys = new Set([
    ...Object.keys(aiResultsByField),
    ...Object.keys(regularResultsByField),
  ]);

  const fieldComparisons: FieldComparisonData[] = [];

  allFieldKeys.forEach((fieldKey) => {
    if (fieldKey === 'unknown') return; // Skip unknown fields

    const aiResults = aiResultsByField[fieldKey] || [];
    const regularResults = regularResultsByField[fieldKey] || [];

    const { changeType, improvementType } = categorizeFieldChanges(
      aiResults,
      regularResults
    );

    const { errors: aiErrors, warnings: aiWarnings } =
      filterResultsByType(aiResults);
    const { errors: regularErrors, warnings: regularWarnings } =
      filterResultsByType(regularResults);

    const aiErrorCount = aiErrors.length;
    const regularErrorCount = regularErrors.length;
    const aiWarningCount = aiWarnings.length;
    const regularWarningCount = regularWarnings.length;

    const reductionCount =
      regularErrorCount + regularWarningCount - (aiErrorCount + aiWarningCount);

    const documentVersionFieldUuid =
      aiResults[0]?.documentVersionFieldUuid ||
      regularResults[0]?.documentVersionFieldUuid;

    fieldComparisons.push({
      fieldKey,
      fieldLabel: fieldKeyToLabel[fieldKey] || fieldKey,
      aiResults,
      regularResults,
      changeType,
      improvementType,
      aiErrorCount,
      regularErrorCount,
      aiWarningCount,
      regularWarningCount,
      reductionCount,
      documentVersionFieldUuid,
    });
  });

  // Calculate summary stats
  const improvedFields = fieldComparisons.filter(
    (f) =>
      f.changeType === 'removed' ||
      (f.changeType === 'modified' && f.reductionCount > 0)
  ).length;

  const degradedFields = fieldComparisons.filter(
    (f) =>
      f.changeType === 'added' ||
      (f.changeType === 'modified' && f.reductionCount < 0)
  ).length;

  const unchangedFields = fieldComparisons.filter(
    (f) => f.changeType === 'unchanged'
  ).length;

  const hasAnyChanges = fieldComparisons.some(
    (f) => f.changeType !== 'unchanged'
  );

  return {
    fieldComparisons: fieldComparisons.sort((a, b) => {
      // Sort by impact: improved first, then degraded, then unchanged
      const aScore =
        a.changeType === 'removed'
          ? 3
          : a.changeType === 'modified' && a.reductionCount > 0
            ? 2
            : a.changeType === 'added'
              ? 1
              : 0;
      const bScore =
        b.changeType === 'removed'
          ? 3
          : b.changeType === 'modified' && b.reductionCount > 0
            ? 2
            : b.changeType === 'added'
              ? 1
              : 0;

      return bScore - aScore;
    }),
    totalFields: fieldComparisons.length,
    improvedFields,
    unchangedFields,
    degradedFields,
    hasAnyChanges,
  };
}
